import { Head, Link } from "@inertiajs/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { sessionPath, settingsSessionsPath } from "@/routes"
import type { BreadcrumbItem, Session } from "@/types"

interface SessionsProps {
  sessions: Session[]
}

export default function Sessions({ sessions }: SessionsProps) {
  const { t } = useTranslation()
  const { currentSession } = useCurrentContext()
  const currentSessionId = currentSession?.id ?? null

  const breadcrumbs: BreadcrumbItem[] = useMemo(
    () => [
      {
        title: t("settings.sessions.breadcrumb"),
        href: settingsSessionsPath(),
      },
    ],
    [t],
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={t("settings.sessions.title")}
            description={t("settings.sessions.description")}
          />

          <div className="space-y-4">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="flex flex-col space-y-2 rounded-lg border p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">
                      {session.user_agent}
                      {session.id === currentSessionId && (
                        <Badge variant="secondary" className="ml-2">
                          {t("settings.sessions.current_badge")}
                        </Badge>
                      )}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {t("settings.sessions.ip_prefix")} {session.ip_address}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {t("settings.sessions.active_since")}{" "}
                      {new Date(session.created_at).toLocaleString()}
                    </p>
                  </div>
                  {session.id !== currentSessionId && (
                    <Button variant="destructive" asChild>
                      <Link
                        method="delete"
                        href={sessionPath({ id: session.id })}
                        as="button"
                      >
                        {t("settings.sessions.logout")}
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
